/**
 * 文字变形对话框（对比 Photoshop 补全第一档缺口 阶段 5）。
 *
 * 复用 `SelectionModifyDialog.tsx` 的模态壳结构（同一份 CSS）——样式下拉
 * （无/弧形/凸起/波浪）+ 弯曲度滑杆，比图层样式那套列表+详情双栏布局
 * 简单得多，不值得再抄一遍那套声明式字段系统。
 */
import type { JSX } from 'solid-js';
import { For, Show, createEffect, createSignal } from 'solid-js';
import { useFocusTrap } from '../a11y/focus-trap';
import type { WarpStyle } from '../../engine/text/warp';
import styles from './SelectionModifyDialog.module.css';

const STYLE_OPTIONS: ReadonlyArray<{ readonly value: WarpStyle | 'none'; readonly label: string }> = [
  { value: 'none', label: '无' },
  { value: 'arc', label: '弧形' },
  { value: 'bulge', label: '凸起' },
  { value: 'wave', label: '波浪' },
];

export interface WarpTextDialogState {
  readonly style: WarpStyle | 'none';
  readonly bend: number;
}

export interface WarpTextDialogProps {
  /** null = 对话框关闭；非 null 时是当前文本图层已有的变形（没有就是 'none'/0）。 */
  readonly initial: WarpTextDialogState | null;
  readonly onClose: () => void;
  readonly onConfirm: (state: WarpTextDialogState) => void;
}

export function WarpTextDialog(props: WarpTextDialogProps): JSX.Element {
  const [style, setStyle] = createSignal<WarpStyle | 'none'>('none');
  const [bend, setBend] = createSignal(0);
  let dialogRef: HTMLDivElement | undefined;
  useFocusTrap(() => props.initial !== null, () => dialogRef ?? null, props.onClose);

  createEffect(() => {
    const initial = props.initial;
    if (initial !== null) {
      setStyle(initial.style);
      setBend(initial.bend);
    }
  });

  return (
    <Show when={props.initial !== null}>
      <div class={styles.backdrop} onClick={props.onClose} data-testid="warp-text-backdrop">
        <div
          class={styles.dialog}
          role="dialog"
          ref={(el) => (dialogRef = el)}
          aria-label="文字变形"
          data-testid="warp-text-dialog"
          onClick={(event) => {
            event.stopPropagation();
          }}
        >
          <h2 class={styles.title}>文字变形</h2>

          <label class={styles.row}>
            <span class={styles.label}>样式</span>
            <select
              class={styles.number}
              value={style()}
              data-testid="warp-text-style"
              onChange={(event) => setStyle(event.currentTarget.value as WarpStyle | 'none')}
            >
              <For each={STYLE_OPTIONS}>{(option) => <option value={option.value}>{option.label}</option>}</For>
            </select>
          </label>

          <label class={styles.row}>
            <span class={styles.label}>弯曲度</span>
            <input
              type="range"
              class={styles.number}
              min="-100"
              max="100"
              value={bend()}
              disabled={style() === 'none'}
              data-testid="warp-text-bend"
              onInput={(event) => setBend(Number(event.currentTarget.value))}
            />
            <span class={styles.value}>{bend()}</span>
          </label>

          <div class={styles.actions}>
            <button type="button" class={styles.secondary} onClick={props.onClose}>
              取消
            </button>
            <button
              type="button"
              class={styles.primary}
              data-testid="warp-text-confirm"
              onClick={() => {
                props.onConfirm({ style: style(), bend: bend() });
              }}
            >
              确定
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
}
